import Controller, { OnBeforeCancel, OnBeforeSubmit, OnClick, OnMount, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import { ActionAPI, FormAPI, InputAPI, ModalProps, ModalService, ToastService } from '@philips-internal/oswald-ui-components';
import BaseController from '../../../BaseController';
import { FragmentNames, ScoreMewsEditAliases } from '../../../../codegen/index';
import moment from 'moment';
import { ISO_FULL_DATE_FORMAT } from '../../../../constants/date-utils';
import timeService from '../../../../services/time-service';
import { Inject} from '@odinjs/odin';
import InformationBarUtils from '../../../commons/header/information-bar-header-utils';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import { Value } from '@philips-internal/oswald-ui-core';
import { LocatorTypes } from '../../../locator/locator-controller';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { Endpoint } from '../../../../constants/endpoints';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { generateUUID } from '@philips-internal/oswald-ui-utils';
import { ApplicationRoute } from '../../../../constants/routes';
import { HealthProfessionalUser } from '../../../../app/authentication';
import { DomainType, ResponseCreateScoreMEWS, ScoreMEWSResponse } from '../../score-types';
import DialogService from '../../../../services/dialog-service';
import { ScoreListContext } from '../../scores-list/scores-list-service';
import { ScoreType } from '../../scores-references/score-reference-controller';
import { AbortableCloseEvent } from '@philips-internal/oswald-ui-core/lib/component/types';

@Controller({ component: ScoreMewsEditAliases.SCORE_MEWS_ADD_LAYOUT, fragment: FragmentNames.SCORE_MEWS_EDIT })
export default class ScoreMewsEditController extends BaseController {

  @Inject(ScoreMewsEditAliases.INPUT_SETUP_DATE)
  private setupDateInputApi!: InputAPI;

  @Inject(ScoreMewsEditAliases.FORM_SCORE_MEWS_ADD)
  private formApi!: FormAPI;

  @Inject(ScoreMewsEditAliases.ACTION_DELETE_SCORE_MEWS)
  private actionDeleteAPI!: ActionAPI;

  @Inject(ScoreMewsEditAliases.ACTION_NEW_SCORE_MEWS)
  private actionNewAPI!: ActionAPI;

  @Inject(ScoreMewsEditAliases.ACTION_REFERENCE_SCORE_MEWS)
  private actionReferenceAPI!: ActionAPI;

  @Inject(ScoreMewsEditAliases.ACTION_RELEASE_SCORE_MEWS)
  private actionReleaseAPI!: ActionAPI;

  @OnMount({ target: ScoreMewsEditAliases.FORM_SCORE_MEWS_ADD })
  handleFormMount(): void {
    ScoreListContext.currentTab = ScoreType.SCORE_MEWS
    const { encounterId, idScoreIndexes, patientId } = this.historyService.getCurrentRouteParams();
    applicationContext.popNavigationItem();
    this.formApi.setLoading(true);
    this.loadEditionData(idScoreIndexes as number)
    this.actionDeleteAPI.setEnabled(false);
    this.actionNewAPI.setEnabled(false);
    this.actionReferenceAPI.setEnabled(false);
    this.actionReleaseAPI.setEnabled(false);
    InformationBarUtils.collapsedBar = true;
    this.context.set({ encounterId, locator: LocatorTypes.PROFESSIONAL_PERSON, patientId });
  }

  @OnUnmount({ target: ScoreMewsEditAliases.FORM_SCORE_MEWS_ADD })
  handleUnMountForm(): void {
    this.clearAbortController();
  }

  @OnMount({target: ScoreMewsEditAliases.INPUT_SETUP_DATE})
  handleInputMount(): void {
    timeService.getCurrentDateTime(this.setAbortController())
      .then(date => this.setupDateInputApi.setValue(date))
      .catch(() => this.setupDateInputApi.setValue(moment().format(ISO_FULL_DATE_FORMAT)))
  }

  private showModalSubmit(idScore: number): void {
    DialogService.open({
      descriptionDialog: 'patient-evolution.release-title-evolution',
      onClickCancel: () => { this.goBack() },
      onClickOk: () => { this.releaseMEWScore(idScore) },
      titleDialog: 'patient-evolution.release-evolution',
      titleOnCancel: 'patient-evolution.release-cancel-evolution',
      titleOnOk: 'patient-evolution.release-submit-evolution',
    });
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'score-pews-create',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }

  private goBack(): void {
    applicationContext.popNavigationItem();
    applicationContext.popNavigationItem();
  }

  private loadEditionData(sequenceNumber: number): void {
    const abortController = this.setAbortController();
    HttpService.get(Endpoint.SCORE_MEWS, { sequenceNumber }, {abortController})
      .then(response => {
        const record = response.body as ScoreMEWSResponse;
        const dateEvaluation = String(record.dateEvaluation);

        this.formApi.setEditing(true);
        this.formApi.validate();
        this.formApi.setData({...record, dateEvaluation});

      }).catch((err) => this.showToast(err?.body.errorMessage, ToastLevel.ERROR))
      .finally(() => {
        this.formApi.setLoading(false);
        this.actionDeleteAPI.setEnabled(true);
        this.actionNewAPI.setEnabled(true);
        this.actionReferenceAPI.setEnabled(true);
        this.actionReleaseAPI.setEnabled(true);
      });
  }

  private releaseMEWScore(sequenceNumber: number): void {
    this.formApi.setLoading(true);
    const abortController = this.setAbortController();
    HttpService.put(Endpoint.RELEASE_SCORE_MEWS, {sequenceNumber}, { abortController })
      .then(() => {
        applicationContext.popNavigationItem();
      }).catch((err) => {
        this.showToast(err?.body.errorMessage, ToastLevel.ERROR);
      }).finally(() => {
        this.formApi.setLoading(false);
      });
  }

  private createNewMEWSScore(body: Record<string,Value>): void {
    this.formApi.setLoading(true);
    const abortController = this.setAbortController();
    HttpService.post(Endpoint.POST_CREATE_SCORE_MEWS, undefined, { abortController, body })
      .then(response => {
        const { idScoreIndexes } = response.body as ResponseCreateScoreMEWS;
        this.context.set({ idScoreIndexes, isCreated: true });
        this.showModalSubmit(idScoreIndexes);
      })
      .catch(err => {
        this.showToast(err?.body.errorMessage, ToastLevel.ERROR);
      }).finally(() => this.formApi.setLoading(false));
  }

  private delete(sequenceNumber: number): void {
    this.formApi.setLoading(true);
    const abortController = this.setAbortController();
    HttpService.delete(Endpoint.SCORE_MEWS, {
      sequenceNumber
    }, {abortController}).then(() => {
      this.formApi.setLoading(false);
      applicationContext.popNavigationItem();
    }).catch((err) => {
      this.showToast(err?.body.errorMessage, ToastLevel.ERROR);
      this.formApi.setLoading(false)
    });
  }

  private confirmDelete(idScore: number): void {
    DialogService.open({
      descriptionDialog: 'patient-evolution.delete-question-evolution',
      onClickCancel: () => { this.goBack() },
      onClickOk: () => { this.delete(idScore) },
      titleDialog: 'patient-evolution.delete-question-evolution',
      titleOnCancel: 'tws.form.cancel',
      titleOnOk: 'patient-evolution.delete-evolution',
    });
  }

  @OnBeforeSubmit({ target: ScoreMewsEditAliases.FORM_SCORE_MEWS_ADD })
  handleBeforeSubmit(): void {
    const isCreated = this.context.get('isCreated');
    const {encounterId, idScoreIndexes} = this.historyService.getCurrentRouteParams();
    if (isCreated) {
      this.showModalSubmit(idScoreIndexes as number);
      return;
    }
    const formData = this.formApi.getRecord();
    const levelConsciousness = formData.consciousnessLevel as DomainType;
    const paSistolica = formData.paSistolica ? formData.paSistolica : '';
    const temperature = formData.temperatura ? formData.temperatura : '';
    const heartRate = formData.freqCardiaca ? formData.freqCardiaca : '';
    const respiratoryRate = formData.freqRespiratoria ? formData.freqRespiratoria : '';
    const user = foundationContext.getUser() as unknown as HealthProfessionalUser;
    const evaluationDate = moment(formData.dateEvaluation as string).utc().format(ISO_FULL_DATE_FORMAT);

    const body = {
      attendance: encounterId as number,
      evaluationDate,
      freqCardiaca: heartRate,
      freqRespiratoria: respiratoryRate,
      idScoreIndexes: idScoreIndexes as number,
      nivelConsciencia: levelConsciousness ? levelConsciousness.valueDomain : '',
      paSistolica,
      professionalCode: user.physicalPersonId,
      temperatura: temperature,
    }
    this.createNewMEWSScore(body);
  }

  private getReference(): void {
    const scoreType = 'mews';
    const modalProperties: ModalProps = {
      closeable: true,
      closeByDocument: false,
      closeByEscape: false,
      focusOnOpen: false,
      fragment: 'score-reference',
      id: 'score-reference',
      onCancel: () => { },
      onSubmit: () => { },
      title: 'tws.reference-pain-scale-value',
      uuid: generateUUID(),
    };

    modalProperties.contextData = {
      scoreType,
    }

    ModalService.openFullscreen(modalProperties);
  }

  private discardChanges(): void {
    DialogService.open({
      descriptionDialog: 'clinical-notes.discard-changes',
      onClickCancel: () => {},
      onClickOk: () => {this.goBack();},
      titleDialog: 'clinical-notes.discard-changes',
      titleOnCancel: 'patient-evolution.cancel',
      titleOnOk: 'clinical-notes.discard-submit'
    });
  }

  @OnClick({ target: ScoreMewsEditAliases.ACTION_NEW_SCORE_MEWS })
  handleClickNew(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    applicationContext.popNavigationItem();
    applicationContext.redirect(ApplicationRoute.SCORE_MEWS_CREATE, {
      encounterId,
      patientId,
    })
  }

  @OnClick({ target: ScoreMewsEditAliases.ACTION_RELEASE_SCORE_MEWS })
  handleClickRelease(): void {
    const { idScoreIndexes } = this.historyService.getCurrentRouteParams();
    this.showModalSubmit(idScoreIndexes as number);
  }

  @OnClick({ target: ScoreMewsEditAliases.ACTION_DELETE_SCORE_MEWS })
  handleClickDelete(): void {
    const { idScoreIndexes } = this.historyService.getCurrentRouteParams();
    this.confirmDelete(idScoreIndexes as number);
  }

  @OnClick({ target: ScoreMewsEditAliases.ACTION_REFERENCE_SCORE_MEWS })
  handleClickReference(): void {
    this.getReference();
  }

  @OnUnmount(ScoreMewsEditAliases.SCORE_MEWS_ADD_LAYOUT)
  handleUnmount(): void {
    ScoreListContext.clean();
    ScoreListContext.currentTab = ScoreType.SCORE_MEWS;
  }

  @OnBeforeCancel(ScoreMewsEditAliases.FORM_SCORE_MEWS_ADD)
  onCancelForm(event: AbortableCloseEvent): void {
    event.abort();
    this.discardChanges();
  }
}
